import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Knowledge Base - Quick Start Guide
 */
const QuickStartPage = () => {
  const steps = [
    {
      number: "1",
      title: "Create Your Account",
      description: "Sign up for Videxa and complete your organization profile.",
      details: [
        "Visit the registration page and enter your email",
        "Verify your email address",
        "Complete your organization profile with basic information",
        "Set up your administrator account credentials"
      ]
    },
    {
      number: "2",
      title: "Configure Your Environment",
      description: "Set up your workspace and connect your systems.",
      details: [
        "Navigate to Settings > Organization",
        "Configure your facility information",
        "Set up user roles and permissions",
        "Connect your EHR system (optional for trial)"
      ]
    },
    {
      number: "3",
      title: "Import Your Data",
      description: "Bring your existing data into Videxa.",
      details: [
        "Use our secure data import wizard",
        "Upload patient and encounter data",
        "Map your data fields to Videxa's format",
        "Validate imported data for accuracy"
      ]
    },
    {
      number: "4",
      title: "Start Using Videxa",
      description: "Begin exploring features and capabilities.",
      details: [
        "Access the Dashboard for an overview",
        "Try the CDI Query Assistant",
        "Explore Revenue Cycle Analytics",
        "Review sample reports and insights"
      ]
    }
  ];

  const quickLinks = [
    { title: "User Guide", description: "Comprehensive documentation", link: "/documentation" },
    { title: "Video Tutorials", description: "Step-by-step walkthroughs", link: "/webinars" },
    { title: "Contact Support", description: "Get help from our team", link: "/contact" }
  ];

  return (
    <div className="quick-start-page">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#0E3056] to-[#1a4a7a] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/knowledge-base" className="text-blue-300 hover:text-white mb-4 inline-block">
              ← Back to Knowledge Base
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Quick Start Guide</h1>
            <p className="text-xl text-gray-200">
              Get up and running with Videxa in just a few minutes.
            </p>
          </div>
        </div>
      </section>

      {/* Getting Started Steps */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
              Getting Started
            </h2>

            <div className="space-y-8">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 flex gap-6"
                >
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[#0E3056] dark:bg-[#FFC000] text-white dark:text-gray-900 rounded-full flex items-center justify-center text-xl font-bold">
                      {step.number}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      {step.description}
                    </p>
                    <ul className="space-y-2">
                      {step.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start text-gray-700 dark:text-gray-300">
                          <span className="text-green-500 mr-2 mt-1">✓</span>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
              Need More Help?
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {quickLinks.map((item, index) => (
                <Link
                  key={index}
                  to={item.link}
                  className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <h3 className="text-lg font-semibold text-[#0E3056] dark:text-[#FFC000] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {item.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuickStartPage;
